/**
 * ThemeContext - Global Theme Management for VivyaWatch
 * 
 * Provides application-wide theming with support for:
 * - Light themes (iOS Blue, Ocean, Sunset, Forest, Berry, Rose Gold)
 * - Dark themes (Midnight Dark, Ocean Dark, Carbon Dark)
 * - Liquid Glass design system
 * - localStorage persistence
 * - WCAG 2.2 accessibility compliance
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Theme definitions - Light and Dark themes
const THEMES = {
  // ============ LIGHT THEMES ============
  ios: {
    id: 'ios',
    name: 'iOS Blue',
    icon: 'sparkles',
    description: 'Classic Apple iOS blue',
    mode: 'light',
    background: 'from-slate-100 via-blue-50 to-purple-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-blue-400/30 to-purple-400/20',
      'from-cyan-400/25 to-blue-400/15',
      'from-purple-400/20 to-pink-400/15'
    ],
    primary: 'blue',
    secondary: 'purple',
    accent: 'cyan',
    buttonGradient: 'from-blue-500 to-purple-500',
    buttonHover: 'from-blue-600 to-purple-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#007AFF', '#5856D6', '#34C759', '#AF52DE', '#FF9500', '#FF2D55'],
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Breeze',
    icon: 'waves',
    description: 'Cool cyan and teal',
    mode: 'light',
    background: 'from-cyan-50 via-teal-50 to-blue-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-cyan-400/30 to-teal-400/20',
      'from-blue-400/25 to-sky-400/15',
      'from-teal-400/20 to-emerald-400/15'
    ],
    primary: 'cyan',
    secondary: 'teal',
    accent: 'blue',
    buttonGradient: 'from-cyan-500 to-teal-500',
    buttonHover: 'from-cyan-600 to-teal-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#06B6D4', '#14B8A6', '#3B82F6', '#0EA5E9', '#22D3EE', '#2DD4BF'],
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Glow',
    icon: 'sunset',
    description: 'Warm orange and rose',
    mode: 'light',
    background: 'from-orange-50 via-rose-50 to-pink-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-orange-400/30 to-amber-400/20',
      'from-rose-400/25 to-pink-400/15',
      'from-pink-400/20 to-fuchsia-400/15'
    ],
    primary: 'orange',
    secondary: 'rose',
    accent: 'pink',
    buttonGradient: 'from-orange-500 to-rose-500',
    buttonHover: 'from-orange-600 to-rose-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#F97316', '#FB7185', '#EC4899', '#F59E0B', '#E11D48', '#D946EF'],
  },
  forest: {
    id: 'forest',
    name: 'Forest Green',
    icon: 'trees',
    description: 'Natural green and emerald',
    mode: 'light',
    background: 'from-green-50 via-emerald-50 to-teal-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-green-400/30 to-emerald-400/20',
      'from-teal-400/25 to-cyan-400/15',
      'from-emerald-400/20 to-lime-400/15'
    ],
    primary: 'green',
    secondary: 'emerald',
    accent: 'teal',
    buttonGradient: 'from-green-500 to-emerald-500',
    buttonHover: 'from-green-600 to-emerald-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#22C55E', '#10B981', '#14B8A6', '#34D399', '#4ADE80', '#2DD4BF'],
  },
  berry: {
    id: 'berry',
    name: 'Berry Purple',
    icon: 'cherry',
    description: 'Rich purple and pink',
    mode: 'light',
    background: 'from-purple-50 via-fuchsia-50 to-pink-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-purple-400/30 to-fuchsia-400/20',
      'from-pink-400/25 to-rose-400/15',
      'from-fuchsia-400/20 to-violet-400/15'
    ],
    primary: 'purple',
    secondary: 'fuchsia',
    accent: 'pink',
    buttonGradient: 'from-purple-500 to-fuchsia-500',
    buttonHover: 'from-purple-600 to-fuchsia-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#A855F7', '#D946EF', '#EC4899', '#8B5CF6', '#C026D3', '#F472B6'],
  },
  rose: {
    id: 'rose',
    name: 'Rose Gold',
    icon: 'gem',
    description: 'Elegant rose and amber',
    mode: 'light',
    background: 'from-rose-50 via-amber-50 to-orange-50',
    cardBg: 'bg-white/70',
    cardBorder: 'border-white/50',
    textPrimary: 'text-gray-900',
    textSecondary: 'text-gray-700',
    textMuted: 'text-gray-600 dark:text-gray-200',
    orbs: [
      'from-rose-400/30 to-pink-400/20',
      'from-amber-400/25 to-yellow-400/15',
      'from-orange-400/20 to-rose-400/15'
    ],
    primary: 'rose',
    secondary: 'amber',
    accent: 'orange',
    buttonGradient: 'from-rose-500 to-amber-500',
    buttonHover: 'from-rose-600 to-amber-600',
    headerBg: 'bg-white/80 backdrop-blur-xl border-white/50',
    sidebarBg: 'bg-white/70 backdrop-blur-xl',
    chartColors: ['#F43F5E', '#F59E0B', '#FB923C', '#FDA4AF', '#FCD34D', '#FDBA74'],
  },

  // ============ DARK THEMES ============
  midnightDark: {
    id: 'midnightDark',
    name: 'Midnight Dark',
    icon: 'moon',
    description: 'Deep indigo and violet',
    mode: 'dark',
    background: 'from-slate-950 via-indigo-950 to-slate-900',
    cardBg: 'bg-slate-800/70',
    cardBorder: 'border-slate-700/50',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-200',
    textMuted: 'text-gray-400',
    orbs: [
      'from-indigo-600/20 to-violet-600/10',
      'from-purple-600/15 to-blue-600/10',
      'from-blue-600/15 to-cyan-600/10'
    ],
    primary: 'indigo',
    secondary: 'violet',
    accent: 'purple',
    buttonGradient: 'from-indigo-500 to-violet-500',
    buttonHover: 'from-indigo-400 to-violet-400',
    headerBg: 'bg-slate-900/90 backdrop-blur-xl border-slate-700/50',
    sidebarBg: 'bg-slate-900/80 backdrop-blur-xl',
    chartColors: ['#818CF8', '#A78BFA', '#C084FC', '#6366F1', '#8B5CF6', '#A855F7'],
  },
  oceanDark: {
    id: 'oceanDark',
    name: 'Ocean Dark',
    icon: 'waves',
    description: 'Deep teal and cyan',
    mode: 'dark',
    background: 'from-slate-950 via-cyan-950 to-teal-950',
    cardBg: 'bg-slate-800/70',
    cardBorder: 'border-slate-700/50',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-200',
    textMuted: 'text-gray-400',
    orbs: [
      'from-cyan-600/20 to-teal-600/10',
      'from-teal-600/15 to-emerald-600/10',
      'from-blue-600/15 to-cyan-600/10'
    ],
    primary: 'cyan',
    secondary: 'teal',
    accent: 'emerald',
    buttonGradient: 'from-cyan-500 to-teal-500',
    buttonHover: 'from-cyan-400 to-teal-400',
    headerBg: 'bg-slate-900/90 backdrop-blur-xl border-slate-700/50',
    sidebarBg: 'bg-slate-900/80 backdrop-blur-xl',
    chartColors: ['#22D3EE', '#2DD4BF', '#34D399', '#06B6D4', '#14B8A6', '#10B981'],
  },
  carbonDark: {
    id: 'carbonDark',
    name: 'Carbon Dark',
    icon: 'moon',
    description: 'Classic dark gray',
    mode: 'dark',
    background: 'from-gray-950 via-gray-900 to-gray-950',
    cardBg: 'bg-gray-800/70',
    cardBorder: 'border-gray-700/50',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-200',
    textMuted: 'text-gray-400',
    orbs: [
      'from-gray-600/20 to-gray-500/10',
      'from-slate-600/15 to-gray-600/10',
      'from-zinc-600/15 to-slate-600/10'
    ],
    primary: 'gray',
    secondary: 'slate',
    accent: 'zinc',
    buttonGradient: 'from-gray-600 to-gray-700',
    buttonHover: 'from-gray-500 to-gray-600',
    headerBg: 'bg-gray-900/90 backdrop-blur-xl border-gray-700/50',
    sidebarBg: 'bg-gray-900/80 backdrop-blur-xl',
    chartColors: ['#9CA3AF', '#A1A1AA', '#D1D5DB', '#71717A', '#E5E7EB', '#A3A3A3'],
  },
};

export { THEMES };

// Create context
const ThemeContext = createContext(undefined);

// Theme Provider component
// Helper to check if current route is a public page (should NOT apply dark mode)
const isPublicPage = () => {
  const pathname = window.location.pathname;
  // Exact match paths
  const exactPublicPaths = ['/', '/signin', '/signup', '/pricing', '/features', '/solutions', '/partner', '/contact', '/privacy-policy', '/security-compliance', '/screenshot-monitoring', '/productivity-analytics', '/compliance-suite', '/insider-risk', '/compare'];
  // Prefix match paths (for nested routes like /resources/privacy/...)
  const prefixPublicPaths = ['/resources', '/tryus'];

  return exactPublicPaths.includes(pathname) ||
         pathname === '/' ||
         prefixPublicPaths.some(prefix => pathname.startsWith(prefix));
};

export const ThemeProvider = ({ children }) => {
  const [themeId, setThemeId] = useState('ios');
  const [theme, setTheme] = useState(THEMES.ios);

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('vivyawatch-theme');
    if (savedTheme && THEMES[savedTheme]) {
      setThemeId(savedTheme);
      setTheme(THEMES[savedTheme]);
    }
  }, []);

  // Change theme function
  const changeTheme = useCallback((newThemeId) => {
    if (THEMES[newThemeId]) {
      setThemeId(newThemeId);
      setTheme(THEMES[newThemeId]);
      localStorage.setItem('vivyawatch-theme', newThemeId);

      // Only apply dark mode class on authenticated (non-public) pages
      if (!isPublicPage()) {
        if (THEMES[newThemeId].mode === 'dark') {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
    }
  }, []);

  // Helper function to apply or remove dark mode based on page type
  const updateDarkModeClass = useCallback(() => {
    if (isPublicPage()) {
      // Always remove dark mode on public pages (sign-in, homepage, etc.)
      document.documentElement.classList.remove('dark');
    } else if (theme.mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme.mode]);

  // Apply theme on initial load and when theme changes - only on authenticated pages
  useEffect(() => {
    updateDarkModeClass();
  }, [updateDarkModeClass]);

  // Listen for route changes to apply/remove dark mode when navigating
  useEffect(() => {
    // Listen for popstate (browser back/forward)
    const handlePopState = () => updateDarkModeClass();
    window.addEventListener('popstate', handlePopState);

    // Also create a MutationObserver to detect URL changes from React Router
    let lastPath = window.location.pathname;
    const checkPathChange = () => {
      if (window.location.pathname !== lastPath) {
        lastPath = window.location.pathname;
        updateDarkModeClass();
      }
    };
    const pathCheckInterval = setInterval(checkPathChange, 100);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      clearInterval(pathCheckInterval);
    };
  }, [updateDarkModeClass]);

  const value = {
    theme,
    themeId,
    themes: THEMES,
    changeTheme,
    isDark: theme.mode === 'dark',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;

